import React, { useState, useEffect } from 'react';
import { shopAPI } from '../utils/api';
import { toast } from 'react-toastify';
import './Shop.css';

const STATUS_LABELS = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  abgeschlossen: 'Abgeschlossen'
};

const CART_KEY = 'efc-shop-cart';

const loadCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const saveCart = (cart) => {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
};

const Shop = () => {
  const [activeSection, setActiveSection] = useState('sortiment');
  const [products, setProducts] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [cart, setCart] = useState(loadCart);
  const [selection, setSelection] = useState({});
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const loadProducts = async () => {
    try {
      const response = await shopAPI.getProducts();
      if (response.success) setProducts(response.products || []);
    } catch (error) {
      console.error('Fehler beim Laden des Sortiments:', error);
      toast.error('Das Sortiment konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadMyOrders = async () => {
    try {
      const response = await shopAPI.getMyOrders();
      if (response.success) setMyOrders(response.orders || []);
    } catch (error) {
      console.error('Fehler beim Laden der Bestellungen:', error);
      toast.error('Deine Bestellungen konnten nicht geladen werden');
    }
  };

  useEffect(() => {
    loadProducts();
    loadMyOrders();
  }, []);

  // Grossansicht mit Escape schliessen
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const setSelectionFor = (productId, field, value) => {
    setSelection(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [field]: value }
    }));
  };

  const addToCart = (product) => {
    const chosen = selection[product.id] || {};
    const size = chosen.size || '';
    const quantity = Number(chosen.quantity || 1);

    if (!size) {
      toast.error('Bitte wähle eine Größe');
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) {
      toast.error('Die Menge muss zwischen 1 und 99 liegen');
      return;
    }

    setCart(prev => {
      // Gleiche Kombination aus Artikel und Groesse erhoeht die Menge,
      // statt eine zweite Position anzulegen
      const index = prev.findIndex(i => i.productId === product.id && i.size === size);
      const next = [...prev];
      if (index >= 0) {
        next[index] = { ...next[index], quantity: Math.min(99, next[index].quantity + quantity) };
      } else {
        next.push({
          productId: product.id,
          productName: product.name,
          unitPrice: product.price,
          size,
          quantity
        });
      }
      saveCart(next);
      return next;
    });

    toast.success(`${product.name} (${size}) im Warenkorb`);
  };

  const removeFromCart = (index) => {
    setCart(prev => {
      const next = prev.filter((_, i) => i !== index);
      saveCart(next);
      return next;
    });
  };

  const cartTotal = cart.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);

  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const response = await shopAPI.createOrder(
        cart.map(i => ({ productId: i.productId, size: i.size, quantity: i.quantity })),
        note
      );

      if (response.emailSent === false) {
        toast.info('Deine Bestellung ist angekommen. Die Bestätigungsmail konnte nicht verschickt werden.');
      } else {
        toast.success('Bestellung aufgegeben! Du bekommst gleich eine Bestätigung per Mail.');
      }

      setCart([]);
      saveCart([]);
      setNote('');
      setSelection({});
      setActiveSection('bestellungen');
      await loadMyOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Bestellung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shop-page">
      <div className="container">
        <div className="shop-header">
          <h1>Klamotten</h1>
          <p>Textilien für Mitglieder. Bezahlt wird direkt beim Vorstand.</p>
        </div>

        <div className="shop-tabs">
          <button
            className={`shop-tab ${activeSection === 'sortiment' ? 'active' : ''}`}
            onClick={() => setActiveSection('sortiment')}
          >
            Sortiment
          </button>
          <button
            className={`shop-tab ${activeSection === 'bestellungen' ? 'active' : ''}`}
            onClick={() => setActiveSection('bestellungen')}
          >
            Meine Bestellungen{myOrders.length > 0 ? ` (${myOrders.length})` : ''}
          </button>
        </div>

        {activeSection === 'sortiment' && (
          <>
            {loading ? (
              <p>Sortiment wird geladen …</p>
            ) : products.length === 0 ? (
              <div className="shop-empty">
                <p>Aktuell gibt es nichts zu bestellen. Schau später nochmal vorbei.</p>
              </div>
            ) : (
              <div className="shop-grid">
                {products.map(product => (
                  <div key={product.id} className="shop-card">
                    {product.imageUrl && (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="shop-card-image"
                        onClick={() => setLightbox(product)}
                        title="Bild vergrößern"
                      />
                    )}
                    <div className="shop-card-body">
                      <h3>{product.name}</h3>
                      {product.description && <p className="shop-card-text">{product.description}</p>}
                      <p className="shop-card-price">{product.price.toFixed(2)} €</p>

                      <div className="shop-card-controls">
                        <select
                          value={selection[product.id]?.size || ''}
                          onChange={(e) => setSelectionFor(product.id, 'size', e.target.value)}
                          aria-label="Größe"
                        >
                          <option value="">Größe wählen</option>
                          {product.sizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={selection[product.id]?.quantity || 1}
                          onChange={(e) => setSelectionFor(product.id, 'quantity', e.target.value)}
                          aria-label="Menge"
                        />
                        <button className="btn btn-primary" onClick={() => addToCart(product)}>
                          In den Warenkorb
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {cart.length > 0 && (
              <div className="shop-cart">
                <h2>Dein Warenkorb</h2>
                <ul className="shop-cart-list">
                  {cart.map((item, index) => (
                    <li key={`${item.productId}-${item.size}`}>
                      <span>
                        {item.quantity}× {item.productName} (Größe {item.size})
                      </span>
                      <span className="shop-cart-line">
                        {(item.unitPrice * item.quantity).toFixed(2)} €
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => removeFromCart(index)}
                          title="Entfernen"
                        >
                          ✕
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="form-group">
                  <label htmlFor="shopNote">Nachricht an den Vorstand (optional)</label>
                  <textarea
                    id="shopNote"
                    rows="2"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="z.B. Wunschtermin zur Abholung"
                  />
                </div>

                <div className="shop-cart-foot">
                  <strong>Gesamt: {cartTotal.toFixed(2)} €</strong>
                  <button className="btn btn-primary" onClick={submitOrder} disabled={submitting}>
                    {submitting ? 'Wird gesendet …' : 'Bestellung abschicken'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {lightbox && (
          <div
            className="shop-lightbox"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-label={`${lightbox.name} vergrößert`}
          >
            <button
              className="shop-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Schließen"
            >
              ✕
            </button>
            <img src={lightbox.imageUrl} alt={lightbox.name} onClick={(e) => e.stopPropagation()} />
            <div className="shop-lightbox-caption">
              {lightbox.name} — {lightbox.price.toFixed(2)} €
            </div>
          </div>
        )}

        {activeSection === 'bestellungen' && (
          myOrders.length === 0 ? (
            <div className="shop-empty">
              <p>Du hast noch nichts bestellt.</p>
            </div>
          ) : (
            <div className="shop-orders">
              {myOrders.map(order => (
                <div key={order.id} className="shop-order-card">
                  <div className="shop-order-head">
                    <strong>Bestellung Nr. {order.id}</strong>
                    <span>{new Date(order.createdAt).toLocaleDateString('de-DE')}</span>
                  </div>
                  <ul className="shop-order-items">
                    {order.items.map(item => (
                      <li key={item.id}>
                        {item.quantity}× {item.productName} (Größe {item.size}) —{' '}
                        {(item.unitPrice * item.quantity).toFixed(2)} €
                      </li>
                    ))}
                  </ul>
                  {order.note && <p className="shop-order-note">Deine Nachricht: {order.note}</p>}
                  <div className="shop-order-foot">
                    <span className="shop-order-total">Gesamt: {order.total.toFixed(2)} €</span>
                    <span className="shop-badge">{STATUS_LABELS[order.status] || order.status}</span>
                    <span className={`shop-badge ${order.paid ? 'is-paid' : 'is-open'}`}>
                      {order.paid ? 'Bezahlt' : 'Zahlung offen'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Shop;
